import { RowState } from './dataset.js';
import { safeColor } from './cellfx.js';
import { NIR, isUntouchedRow, CHECKISH } from './grid-util.js';
import { normValidation } from './input.js';
export class GridNewRow {
    /** 새 항목 행 설정 (꺼져 있거나 서버 행 모델·카드 보기면 null) */
    /** @internal */ nirOpt() {
        const o = this.opts.newItemRow;
        if (!o || !this.opts.editable || this.srm)
            return null;
        const x = typeof o === 'object' ? o : {};
        return { position: o === 'bottom' || x.position === 'bottom' ? 'bottom' : 'top', infoText: x.infoText ?? this.t('여기를 클릭해 새 행 추가'), separatorColor: x.separatorColor, separatorWidth: x.separatorWidth };
    }
    /** 새 항목 행이 켜져 있나 */
    hasNewItemRow() { return !!this.nirOpt(); }
    /** 새 항목 행에서 입력 중인 행 (없으면 -1) */
    getNewItemRow() { return this.nirRow; }
    /** 새 항목 행에 포커스가 있나 */
    isNewItemRowFocused() { return this.focus.vi === NIR; }
    /** 새 항목 행으로 포커스 (편집 시작은 하지 않음) */
    focusNewItemRow(col) {
        if (!this.nirOpt())
            return false;
        if (this.editing && !this.commitEdit())
            return false;
        const ci = col ? this.cols.findIndex(c => c.id === col) : -1;
        this.setFocus(NIR, ci >= 0 ? ci : this.focus.vi === NIR ? this.focus.ci : this.firstNirCol(), false);
        this.ime.focus({ preventScroll: true });
        return this.focus.vi === NIR;
    }
    /** @internal */ firstNirCol() {
        const i = this.cols.findIndex(c => !c.special && !!this.editorOf(c) && this.columnAccess(c) === 'edit');
        return i >= 0 ? i : this.firstDataCol();
    }
    /** @internal */ focusNir(ci) {
        ci = Math.max(0, Math.min(this.cols.length - 1, ci));
        const was = this.focus.vi === NIR;
        this.focus = { vi: NIR, ci };
        this.anchor = { vi: NIR, ci };
        if (!was && this.form)
            this.hideEditForm(true);
        if (this.extraRanges.length) {
            this.extraRanges = [];
            this.ver++;
        }
        if (!this.editing && !this.composing && this.ime.value)
            this.ime.value = '';
        this.announce();
        this.hideTip();
        this.ensureVisible(NIR, ci);
        this.bump();
        this.emitRowFocus();
        const m = this.nirRow >= 0 ? this.getCellMessage(this.nirRow, this.cols[ci]?.id) : null;
        if (m) {
            this.positionIme();
            this.showTip(m.message, m.level);
        }
    }
    /** 입력 줄의 INSERT 행 만들기 (newRow 기본값) */
    /** @internal */ ensureNirRow() {
        if (this.nirRow >= 0)
            return this.nirRow;
        if (!this.opts.editable || this.protectBlocks('insert'))
            return -1;
        const vals = this.beforeRowInsert(this.nirOpt()?.position === 'bottom' ? this.view.length : 0, this.opts.newRow?.() ?? {}, 'newItemRow');
        if (!vals)
            return -1;
        this.nirRow = this.ds.length; // addRow 가 붙일 물리 행 → onData0 'rowadd' 가 보기에 넣지 않음
        try {
            this.ds.addRow(vals);
        }
        catch (err) {
            this.nirRow = -1;
            throw err;
        }
        this.nirInit = this.ds.getRow(this.nirRow);
        return this.nirRow;
    }
    /** 입력 줄에서 고친 것이 있나 */
    /** @internal */ nirDirty() { return this.nirRow >= 0 && !!this.nirInit && !isUntouchedRow(this.ds.getRow(this.nirRow), this.nirInit); }
    /** 한 행 검증 (필수·validate) — 새 항목 행 확정용 */
    /** @internal */ rowErrorsOf(row) {
        const out = [];
        for (const c of this.cols) {
            if (c.special || c.def.value)
                continue;
            const v = this.ds.get(row, c.id), rule = this.ruleOf(c, row);
            let m = null;
            if ((rule?.required ?? c.def.required) && (v == null || v === ''))
                m = { level: 'error', message: `${c.def.header ?? c.id}은(는) 필수입니다` };
            else {
                const b = this.bytesError(c, v);
                m = b ? { level: 'error', message: b } : normValidation(c.def.validate?.(v, row, this.ds));
            }
            // 오류만 막음 — 경고·안내는 칸에 표시하고 통과 (15차)
            if (m && m.level !== 'error')
                this.warns.set(row + ':' + c.id, m);
            else if (m)
                out.push({ row, col: c.id, message: m.message, level: 'error' });
        }
        return out;
    }
    /**
     * 새 항목 행 확정: 검증(필수·validate) → 목록 끝에 붙이고 입력 줄 비움 → 포커스는 입력 줄 첫 칸 (연속 입력).
     * 검증 실패면 그 칸에 오류를 보이고 false. 고친 게 없으면 아무것도 안 함
     */
    postNewItemRow(validate = true, refocus = true) {
        if (this.editing?.vi === NIR && !this.commitEdit())
            return false;
        const r = this.nirRow;
        if (r < 0) {
            if (refocus && this.focus.vi === NIR)
                this.focusNir(this.firstNirCol());
            return true;
        }
        if (!this.nirDirty()) {
            if (refocus && this.focus.vi === NIR)
                this.focusNir(this.firstNirCol());
            return true;
        }
        if (validate) {
            const errs = this.rowErrorsOf(r);
            if (errs.length) {
                for (const x of errs)
                    this.errors.set(r + ':' + x.col, x.message);
                const ci = this.cols.findIndex(c => c.id === errs[0].col);
                if (this.focus.vi === NIR && ci >= 0) {
                    this.focus = { vi: NIR, ci };
                    this.anchor = { ...this.focus };
                }
                this.bump();
                this.positionIme();
                this.showTip(errs[0].message);
                return false;
            }
        }
        this.nirRow = -1;
        this.nirInit = null;
        this.pushUndo({ label: this.t('새 행 추가'), at: Date.now(), cells: [], rows: [{ row: r, before: RowState.DISCARDED, after: RowState.INSERT }] });
        // 목록 끝에 붙임 (정렬과 상관없이 — 'rowadd' 와 같음)
        if (this.opts.tree || this.isGrouped() || this.extOrder)
            this.rebuild();
        else {
            const v = new Uint32Array(this.view.length + 1);
            v.set(this.view);
            v[this.view.length] = r;
            this.view = v;
            this.disp = new Int32Array(v.buffer);
            this.afterView();
        }
        const vi = this.disp.lastIndexOf(r);
        if (vi >= 0)
            this.ensureVisible(vi, this.focus.ci); // 붙은 행이 보이게 (포커스는 입력 줄)
        if (refocus && this.focus.vi === NIR)
            this.focusNir(this.firstNirCol());
        this.announce();
        this.bump();
        return true;
    }
    /** 입력 중인 새 행 버림 (Esc 두 번·Ctrl+Delete) */
    cancelNewItemRow() {
        if (this.editing?.vi === NIR)
            this.endEdit();
        const r = this.nirRow;
        if (r < 0)
            return;
        this.nirRow = -1;
        this.nirInit = null;
        for (const k of [...this.errors.keys()])
            if (k.startsWith(r + ':'))
                this.errors.delete(k);
        this.clearAsync(x => x === r);
        this.dropUndoOfRow(r);
        if (this.ds.rowState(r) === RowState.INSERT)
            this.ds.deleteRow(r);
        this.hideTip();
        this.bump();
    }
    /** 새 항목 행을 떠날 때: 고친 게 있으면 확정(검증 실패면 false = 머묾), 없으면 버림 */
    /** @internal */ leaveNewItemRow() {
        if (this.focus.vi !== NIR)
            return true;
        if (this.editing && !this.commitEdit())
            return false;
        if (this.nirRow < 0)
            return true;
        if (!this.nirDirty()) {
            this.cancelNewItemRow();
            return true;
        }
        return this.postNewItemRow(true, false);
    }
    /** 입력 줄 안 Tab·Enter: 다음 편집 칸, 끝이면 확정 */
    /** @internal */ nirTab(back) {
        let ci = this.focus.ci;
        for (let g = 0; g < this.cols.length; g++) {
            ci = this.stepCol(ci, back ? -1 : 1);
            if (ci < 0 || ci >= this.cols.length)
                break;
            const c = this.cols[ci];
            if (c.special)
                continue;
            const ok = this.nirRow >= 0 ? this.canEditCol(c, this.nirRow) : !!this.editorOf(c) && this.columnAccess(c) === 'edit';
            if (ok) {
                this.focusNir(ci);
                return;
            }
        }
        if (!back)
            this.postNewItemRow();
    }
    /** 위·아래 이동 (새 항목 행 들고 나기 포함) */
    /** @internal */ vmove(d, ext = false) {
        const vi = this.focus.vi, n = this.disp.length, o = this.nirOpt(), ci = this.focus.ci;
        if (vi === NIR) {
            if (o && n && (o.position === 'top' ? d > 0 : d < 0))
                this.setFocus(o.position === 'top' ? Math.min(n - 1, d - 1) : Math.max(0, n + d), ci, false);
            return;
        }
        if (o && !ext && ((o.position === 'top' && d < 0 && vi + d < 0) || (o.position === 'bottom' && d > 0 && vi + d >= n))) {
            this.setFocus(NIR, ci, false);
            return;
        }
        // 마지막 행에서 ↓ = 새 행 (appendRowOnDown)
        if (d > 0 && !ext && n && vi + d >= n && this.opts.appendRowOnDown && this.opts.editable && !this.srm && !this.cardView && this.appendRowDown())
            return;
        this.setFocus(vi + d, ci, ext);
    }
    /** 새 항목 행 키 (편집 중이 아닐 때) */
    /** @internal */ handleNirKey(e) {
        const ci = this.focus.ci, ctrl = e.ctrlKey || e.metaKey;
        switch (e.key) {
            case 'ArrowDown':
            case 'PageDown':
                this.vmove(1);
                return true;
            case 'ArrowUp':
            case 'PageUp':
                this.vmove(-1);
                return true;
            case 'ArrowRight':
                this.setFocus(NIR, ctrl ? this.cols.length - 1 : Math.min(this.cols.length - 1, this.stepCol(ci, 1)), false);
                return true;
            case 'ArrowLeft':
                this.setFocus(NIR, ctrl ? 0 : Math.max(0, this.stepCol(ci, -1)), false);
                return true;
            case 'Home':
                if (ctrl)
                    return true;
                this.setFocus(NIR, 0, false);
                return true;
            case 'End':
                if (ctrl) {
                    const o = this.nirOpt();
                    if (o?.position === 'top' && this.disp.length)
                        this.setFocus(this.disp.length - 1, ci, false);
                    return true;
                }
                this.setFocus(NIR, this.cols.length - 1, false);
                return true;
            case 'Tab':
                this.nirTab(e.shiftKey);
                return true;
            case 'Enter':
                if (ctrl) {
                    this.postNewItemRow();
                    return true;
                }
                this.moveAfterEnter(e.shiftKey);
                return true;
            case 'F2':
                this.beginEdit('full');
                return true;
            case 'Escape':
                if (this.nirRow >= 0)
                    this.cancelNewItemRow();
                return true;
            case 'Delete': {
                if (ctrl) {
                    this.cancelNewItemRow();
                    return true;
                }
                const c = this.cols[ci];
                if (this.nirRow >= 0 && c && !c.special && this.canEditCol(c, this.nirRow))
                    this.setCellByUser([{ row: this.nirRow, col: c.id, value: null }], this.t('지우기'));
                return true;
            }
            case 'Backspace':
                if (this.beginEdit('quick')) {
                    this.ime.value = '';
                    return true;
                }
                return false;
            case ' ': {
                const c = this.cols[ci];
                if (c && !c.special && CHECKISH.has(this.editorOf(c)) && this.columnAccess(c) === 'edit') {
                    const r = this.ensureNirRow();
                    if (r >= 0 && this.canEditCol(c, r))
                        this.toggleCheckCell(c, r);
                    return true;
                }
                return false;
            }
            case 'Insert': return true;
        }
        if (ctrl && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) {
            this.redo();
            return true;
        }
        if (ctrl && (e.key === 'z' || e.key === 'Z')) {
            this.undo();
            return true;
        }
        if (ctrl && (e.key === 'a' || e.key === 'A'))
            return true;
        return false;
    }
    /** @internal */ onNirMouseDown(ci) {
        if (this.editing) {
            if (this.editing.vi === NIR && ci === this.editing.ci)
                return;
            if (!this.commitEdit())
                return;
        }
        this.setFocus(NIR, ci, false);
        if (this.focus.vi !== NIR)
            return;
        const c = this.cols[ci];
        if (c && !c.special && CHECKISH.has(this.editorOf(c)) && this.columnAccess(c) === 'edit') {
            const r = this.ensureNirRow();
            if (r >= 0 && this.canEditCol(c, r))
                this.toggleCheckCell(c, r);
        }
    }
    /** 입력 줄 상자 위치 (위: 머리글 바로 아래, 아래: 하단 고정 행 아래)·구분선 */
    /** @internal */ placeNir(o) {
        const box = this.pinBox[3], ref = o.position === 'top' ? this.pinBox[0] : this.pinBox[1].nextElementSibling;
        if (o.position === 'top' ? box.nextElementSibling !== ref : box.previousElementSibling !== this.pinBox[1]) {
            if (o.position === 'top')
                this.root.insertBefore(box, this.pinBox[0]);
            else
                this.root.insertBefore(box, ref);
        }
        box.classList.toggle('og-nirb', o.position === 'bottom');
        const w = o.separatorWidth != null ? Math.max(0, Math.min(8, o.separatorWidth | 0)) : null, col = o.separatorColor != null ? safeColor(o.separatorColor) : null;
        const bs = w != null || col ? `${w ?? 2}px solid ${col ?? 'var(--og-accent)'}` : '';
        box.style.borderBottom = o.position === 'top' ? bs : '';
        box.style.borderTop = o.position === 'bottom' ? bs : '';
    }
    /** @internal */ renderNir(sl, rng) {
        const o = this.nirOpt(), box = this.pinBox[3], panes = this.pinPanes[3], pool = this.pinPool[3];
        this.placeNir(o);
        if (box.hidden)
            box.hidden = false;
        const h = this.rh + 'px';
        if (box.style.height !== h)
            box.style.height = h;
        panes[1].firstElementChild.style.transform = `translate3d(${-sl}px,0,0)`;
        if (!pool.length) {
            const mk = (pane) => { const el = document.createElement('div'); el.className = 'og-r'; el.style.top = '0px'; el.style.height = this.rh + 'px'; el.__slot = 0; el.__pinVi = NIR; (pane === 1 ? panes[1].firstElementChild : panes[pane]).appendChild(el); return el; };
            pool.push({ els: [mk(0), mk(1), mk(2)], cells: [[], [], []], slot: 0, key: '' });
        }
        const p = pool[0], m0 = this.cols.findIndex(c => c.pane === 1), c0 = m0 < 0 ? this.cols.length : m0;
        for (const e of p.els)
            if (e.style.height !== h)
                e.style.height = h;
        if (this.nirRow >= 0)
            this.fillRow(p, NIR, c0, this.cols.length - 1, rng, 0);
        else
            this.fillNirEmpty(p, c0);
        const focused = this.focus.vi === NIR;
        p.els[0].className = p.els[1].className = p.els[2].className = 'og-r og-nirr' + (focused ? ' og-fr' : '') + (this.nirRow >= 0 ? ' og-ri' : '');
        const showInfo = this.nirRow < 0 && !(focused && this.editing);
        if (this.nirInfo.hidden === showInfo)
            this.nirInfo.hidden = !showInfo;
        if (showInfo) {
            if (this.nirInfo.textContent !== o.infoText)
                this.nirInfo.textContent = o.infoText;
            this.nirInfo.style.left = this.cols.filter(c => c.special).reduce((a, c) => a + c.width, 0) + 8 + 'px';
        }
    }
    /** 입력 행이 아직 없을 때: 빈 칸들 (포커스 칸 표시) */
    /** @internal */ fillNirEmpty(p, c0) {
        if (p.group) {
            p.group.remove();
            p.group = undefined;
        }
        const run = (pane, from, to) => {
            let k = 0;
            for (let ci = from; ci <= to; ci++) {
                const c = this.cols[ci];
                if (!c || c.pane !== pane)
                    continue;
                const ce = this.cellEl(p, pane, k++), el = ce.el;
                if (el.hidden)
                    el.hidden = false;
                if (ce.sty)
                    this.applyCellStyle(ce, null);
                if (ce.htmlStr) {
                    ce.html.innerHTML = '';
                    ce.htmlStr = '';
                }
                ;
                if (ce.cust)
                    this.dropCust(ce);
                if (ce.ci !== c.ci) {
                    el.style.left = c.left + 'px';
                    el.style.width = c.width + 'px';
                    ce.ci = c.ci;
                    el.__ci = c.ci;
                    if (c.lh) {
                        el.style.top = c.lt * this.lineH + 'px';
                        el.style.height = c.lh * this.lineH + 'px';
                    }
                    else if (el.style.height)
                        el.style.top = el.style.height = '';
                }
                const foc = this.focus.vi === NIR && c.ci === this.focus.ci;
                const cls = 'og-c' + (c.special ? ' og-sp' + (c.special === 'rownum' ? ' og-rn' : c.special === 'indicator' ? ' og-ind og-ind-new' : '') : this.editorOf(c) && this.columnAccess(c) === 'edit' ? ' og-ed' : '') + (foc ? ' og-focus' : '');
                if (el.className !== cls)
                    el.className = cls;
                const text = c.special === 'rownum' ? '*' : c.special === 'indicator' ? '✱' : '';
                if (ce.text.nodeValue !== text)
                    ce.text.nodeValue = text;
                if (ce.asel !== foc) {
                    ce.asel = foc;
                    el.setAttribute('aria-selected', String(foc));
                }
            }
            for (; k < p.cells[pane].length; k++)
                p.cells[pane][k].el.hidden = true;
        };
        run(0, 0, this.cols.length - 1);
        run(1, c0, this.cols.length - 1);
        run(2, 0, this.cols.length - 1);
    }
}
